import { useState } from "react";
import { useForm } from "react-hook-form";
import { ChevronDown, GitBranch, HelpCircle, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { HiringProcessTemplateFormValues } from "./schema";

interface PredecessorPickerProps {
  stageIndex: number;
  itemIndex: number;
  form: ReturnType<typeof useForm<HiringProcessTemplateFormValues>>;
}

export function PredecessorPicker({
  stageIndex,
  itemIndex,
  form,
}: PredecessorPickerProps) {
  const [search, setSearch] = useState("");

  const stages = form.watch("stages");
  const myRef = form.watch(`stages.${stageIndex}.items.${itemIndex}.ref`);
  const predecessors =
    form.watch(`stages.${stageIndex}.items.${itemIndex}.predecessors`) ?? [];

  // Flatten to (stageName, title, ref) excluding self.
  const others: Array<{ stageName: string; title: string; ref: string }> = [];
  stages.forEach((stage, sIdx) => {
    stage.items.forEach((item, iIdx) => {
      if (sIdx === stageIndex && iIdx === itemIndex) {
        return;
      }
      const ref = item.ref;
      if (!ref) {
        return;
      }
      others.push({
        stageName: stage.name || `Stage ${sIdx + 1}`,
        title: item.title || `Item ${iIdx + 1}`,
        ref,
      });
    });
  });

  const predecessorByRef = new Map(predecessors.map((p) => [p.ref, p]));

  const toggleDepends = (ref: string) => {
    const next = predecessorByRef.has(ref)
      ? predecessors.filter((p) => p.ref !== ref)
      : [...predecessors, { ref, shareContent: false }];
    form.setValue(
      `stages.${stageIndex}.items.${itemIndex}.predecessors`,
      next,
      { shouldDirty: true },
    );
  };

  const toggleShareContent = (ref: string) => {
    const next = predecessors.map((p) =>
      p.ref === ref ? { ...p, shareContent: !p.shareContent } : p,
    );
    form.setValue(
      `stages.${stageIndex}.items.${itemIndex}.predecessors`,
      next,
      { shouldDirty: true },
    );
  };

  if (!myRef) {
    return null;
  }
  if (others.length === 0) {
    return null;
  }

  // Selected predecessors enriched with their display info.
  const selectedPredecessors = predecessors
    .map((p) => ({ ...p, other: others.find((o) => o.ref === p.ref) }))
    .filter((p) => p.other != null) as Array<{
    ref: string;
    shareContent: boolean;
    other: { stageName: string; title: string; ref: string };
  }>;

  // Group available (unselected) items by stage, filtered by search.
  const q = search.trim().toLowerCase();
  const available = others.filter((o) => {
    if (predecessorByRef.has(o.ref)) {
      return false;
    }
    if (!q) {
      return true;
    }
    return (
      o.title.toLowerCase().includes(q) || o.stageName.toLowerCase().includes(q)
    );
  });

  const stageGroups = available.reduce<
    Map<string, Array<{ stageName: string; title: string; ref: string }>>
  >((acc, o) => {
    const list = acc.get(o.stageName) ?? [];
    list.push(o);
    acc.set(o.stageName, list);
    return acc;
  }, new Map());

  return (
    <Collapsible className="mt-3 border border-gray-100 rounded-md bg-gray-50/40">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100/50"
        >
          <span className="flex items-center gap-1.5">
            <GitBranch size={12} className="text-gray-400" />
            Depends on
            {predecessors.length > 0 && (
              <span className="ml-0.5 text-gray-500">
                · {predecessors.length}
              </span>
            )}
          </span>
          <ChevronDown size={14} className="text-gray-400" />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="px-3 pb-3 pt-2 space-y-2">
        {/* Selected predecessors — shown as rows with share-content toggle */}
        {selectedPredecessors.length > 0 && (
          <div className="space-y-1">
            {selectedPredecessors.map(({ ref, shareContent, other }) => (
              <div
                key={ref}
                className="flex flex-col gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-2 sm:flex-row sm:items-center sm:gap-2 sm:py-1.5"
              >
                {/* Title row — always visible */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <GitBranch size={11} className="text-gray-400 shrink-0" />
                  <span className="min-w-0 text-xs truncate">
                    <span className="text-gray-500">{other.stageName}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="font-medium text-gray-700">
                      {other.title}
                    </span>
                  </span>
                </div>

                {/* Controls row — below title on mobile, inline on sm+ */}
                <div className="flex items-center gap-2 shrink-0 pl-[19px] sm:pl-0">
                  {/* Share content toggle */}
                  <div className="flex items-center gap-1.5">
                    <Checkbox
                      id={`share-content-${ref}`}
                      checked={shareContent}
                      onCheckedChange={() => toggleShareContent(ref)}
                    />
                    <Label
                      htmlFor={`share-content-${ref}`}
                      className="text-xs font-normal text-gray-600 cursor-pointer"
                    >
                      Share content
                    </Label>
                  </div>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle
                          size={11}
                          className="text-gray-300 cursor-help hover:text-gray-400"
                        />
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="max-w-[220px] text-xs leading-snug"
                      >
                        When checked, files and links from this predecessor are
                        passed through and displayed when this item opens.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {/* Remove */}
                  <button
                    type="button"
                    onClick={() => toggleDepends(ref)}
                    className="shrink-0 text-gray-400 hover:text-gray-600"
                    aria-label="Remove dependency"
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Divider + available items to add */}
        {available.length > 0 && (
          <>
            {selectedPredecessors.length > 0 && <Separator className="my-1" />}

            {/* Search — only shown when there are enough items to warrant it */}
            {others.length > 5 && (
              <div className="relative">
                <Search
                  size={12}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter items…"
                  className="pl-7 h-7 py-0.5 text-xs"
                />
              </div>
            )}

            <div className="space-y-2">
              {Array.from(stageGroups.entries()).map(([stageName, items]) => (
                <div key={stageName}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 px-1 mb-1">
                    {stageName}
                  </p>
                  <div className="space-y-0.5">
                    {items.map((o) => (
                      <label
                        key={o.ref}
                        className="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer hover:bg-gray-100/70 text-xs text-gray-700"
                      >
                        <Checkbox
                          checked={false}
                          onCheckedChange={() => toggleDepends(o.ref)}
                          className="h-3.5 w-3.5"
                          aria-label={`Add dependency on ${o.title}`}
                        />
                        <span className="font-medium">{o.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {q && available.length === 0 && (
              <p className="text-xs text-gray-400 px-1">No items match.</p>
            )}
          </>
        )}

        {available.length === 0 && selectedPredecessors.length === 0 && (
          <p className="text-xs text-gray-400">No other items available.</p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
